import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import Tooltip from 'components/tooltips/Tooltip';
import LoadingIcon from 'mdi-react/LoadingIcon';
import moment from 'moment-timezone';
import NotificationSystem from 'rc-notification';
import Button from 'components/buttons/Button';

import { NOTIFICATION_NODE_STYLE } from 'constants/misc';
import { TIME_FORMAT, WEEK_DAYS } from 'constants/config';
import CheckIcon from 'mdi-react/CheckIcon';
import BasicNotification from 'components/Notification';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import { getSchedules, updateSchedule, destroySchedule } from 'redux/actions/scheduleActions';
import DestroyModal from 'components/modals/DestroyModal';
import { toggleDestroyModal } from 'redux/actions/destroyModalActions';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import ScheduleCreate from './ScheduleCreate';
import ScheduleUpdate from './ScheduleUpdate';
import ScheduleListItemExcludedDates from './ScheduleListItemExcludedDates';


class ScheduleList extends Component {
  static propTypes = {
    toggleModal: PropTypes.func,
    getSchedules: PropTypes.func.isRequired,
    toggleDestroyModal: PropTypes.func.isRequired,
    updateSchedule: PropTypes.func.isRequired,
    destroySchedule: PropTypes.func.isRequired,
    schedules: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    sequence: PropTypes.shape({
      id: PropTypes.number,
      account_id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    loadSchedules: PropTypes.bool,
    campaign: PropTypes.shape({
      id: PropTypes.number,
      account: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
  };

  static defaultProps = {
    toggleModal: null,
    loadSchedules: false,
  };

  static humanizeDays(schedule) {
    const daysArray = [];
    WEEK_DAYS.forEach((day) => {
      if (schedule[day]) {
        const shortDay = day.substring(0, 3);
        const capitalizedDay = capitalize(shortDay);
        daysArray.push(capitalizedDay);
      }
    });
    return daysArray.filter(item => item).join(', ');
  }

  static humanizeTime(schedule) {
    return ` - ${moment(schedule.time_from, 'HH:mm:ss').local().format(TIME_FORMAT)}
    - ${moment(schedule.time_to, 'HH:mm:ss').local().format(TIME_FORMAT)}
    ${schedule.timezone}`;
  }

  componentDidMount() {
    // loading schedules only if specified in props (we may reuse this modal)
    // in current situation we don't need to load schedules again because they were fetched in parent modal
    if (this.props.loadSchedules) {
      this.props.getSchedules({ account__id: this.getAccountId() });
    }
    // eslint-disable-next-line no-return-assign
    NotificationSystem.newInstance({ style: { zIndex: '9999' } }, n => this.notification = n);
  }

  componentWillUnmount() {
    this.notification.destroy();
  }

  onSuccessScheduleDestroyed = () => {
    this.props.toggleDestroyModal();
    this.props.getSchedules({ account__id: this.getAccountId() }, true);
  }

  getAccountId() {
    if (this.props.campaign.account) {
      return this.props.campaign.account;
    } else if (this.props.sequence.account_id) {
      return this.props.sequence.account_id;
    }
    return false;
  }

  showNotification = (message) => {
    this.notification.notice({
      content: <BasicNotification
        color="success"
        message={message}
      />,
      duration: 2,
      closable: false,
      style: NOTIFICATION_NODE_STYLE,
      className: 'alert--colored right-up',
    });
  }

  prepareDestroy = schedule => () => {
    this.props.toggleDestroyModal(schedule.id, schedule.name);
  };

  async togglePrimary(schedule) {
    await this.props.updateSchedule(schedule.id, { is_primary: !schedule.is_primary });
    this.showNotification('Successfully updated primary schedule');
    await this.props.getSchedules({ account__id: this.getAccountId() }, true);
  }

  render() {
    const { schedules } = this.props;
    const isSchedulesLoading = schedules.loading;

    return (
      <div className="manage-schedules">
        <DestroyModal
          funcToExecute={this.props.destroySchedule}
          onSuccess={this.onSuccessScheduleDestroyed}
        />
        {(isSchedulesLoading) && <div className="loader"><LoadingIcon /></div>}
        <div className="instance-form__body">

          {!this.props.schedules.loading &&
            this.props.schedules.results.map(schedule => (
              <React.Fragment key={schedule.id}>
                <span className="schedule-name">{ schedule.name }</span>
                <div className={classNames('icons-list', { disabled: schedule.name === 'Default' })}>
                  <Tooltip
                    placement="left"
                    title={schedule.is_primary ? 'Is default' : 'Select as default'}
                    className="manage-schedules__tooltip d-inline"
                  >
                    <div
                      role="button"
                      className="right-icon close-detail"
                      onClick={() => this.togglePrimary(schedule)}
                      tabIndex="-1"
                      onKeyDown={() => {}}
                    >
                      <CheckIcon
                        id={`markAsDefaultTooltip-${schedule.id}`}
                        className={
                          !schedule.is_primary && schedule.name !== 'Default' ? 'svg--not-primary' : 'svg--primary'
                        }
                      />
                    </div>
                  </Tooltip>
                  <ScheduleUpdate
                    accountId={this.getAccountId()}
                    initialValues={{
                      account: schedule.account,
                      scheduleId: schedule.id,
                      name: schedule.name,
                      days_to_send: WEEK_DAYS.filter(day => schedule[day]),
                      time_from: moment(schedule.time_from, 'HH:mm:ss').toDate(),
                      time_to: moment(schedule.time_to, 'HH:mm:ss').toDate(),
                      exclude_us_holidays: schedule.exclude_us_holidays,
                      excluded_dates: schedule.excluded_dates,
                      timezone: { label: schedule.timezone, value: schedule.timezone },
                    }}
                    className="d-inline"
                  />
                  <DeleteOutlineIcon
                    className={schedule.account ? '' : 'disabled'}
                    onClick={schedule.account ? this.prepareDestroy(schedule) : () => {}}
                  />
                </div>
                <p>
                  { ScheduleList.humanizeDays(schedule) }
                  { ScheduleList.humanizeTime(schedule) }
                  { !schedule.exclude_us_holidays && '+ US holidays ' }
                  {/* TODO: fix design for long strings,
                  for example, when all days and custom dates are present it leads to mess */}
                </p>
                <ScheduleListItemExcludedDates schedule={schedule} />
                <hr />
              </React.Fragment>
            ))
          }

        </div>
        <div className="instance-form__footer">
          <ScheduleCreate accountId={this.getAccountId()} className="schedule-create-form__button" />
          <Button type="button" color="primary" variant="contained" onClick={this.props.toggleModal}>Done</Button>
        </div>
      </div>
    );
  }
}

export default compose(connect(
  state => ({
    campaign: state.campaign,
    schedules: state.schedules,
    sequence: state.sequence,
  }),
  {
    getSchedules, updateSchedule, destroySchedule, toggleDestroyModal,
  },
))(ScheduleList);
